import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { filter } from "rxjs/operators";
import { Subscription } from "rxjs";

import { FullComponent } from "../../layouts/full/full.component";
import { RequestService } from "../../services/request.service";
import { SidebarService } from "../../services/sidebar.service";
import { PermissionService } from "../../services/permission.service";
import { AprobacionOrdenCompraWorkspaceService } from "../../services/workspace/aprobacion-orden-compra-workspace.service";
import { MENU_SECTIONS } from "./menu-items";
import { MenuSection, RouteInfo } from "./sidebar.metadata";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
})
export class SidebarComponent implements OnInit, OnDestroy {
  nombre: string;
  rol: string;

  public loadingc = false;
  public innerWidth: any;
  public sections: MenuSection[] = [];
  isTouch = window.matchMedia?.("(hover: none)").matches || ("ontouchstart" in window);
  private flyoutPinned = false;

  searchTerm: string = "";

  // Empresa selector
  empresas: any[] = [];
  filterEmpresas: string = "";

  facturacion = false;
  propiedades = false;

  expanded = new Set<string>();
  private routerSub?: Subscription;
  private aprobacionesContadorSub?: Subscription;

  flyoutRoot: any | null = null;
  flyoutTop = 8;
  flyoutLeft = 80;

  private flyoutCloseTimer: any = null;
  private hoverExpanded = new Set<string>();

  private readonly FLYOUT_WIDTH = 260;
  private readonly FLYOUT_GAP = 10;

  // Iconos nuevos del menu (set de la disenadora). Mapea el nombre semantico
  // del campo `icon` de menu-items.ts al archivo SVG de assets/icons.
  // Si un nombre no esta aqui, el sidebar usa el icono inline anterior
  // (app-sidebar-icon). Ver ICONOS-SIDEBAR.md para detalles y rollback.
  private readonly menuIconFiles: { [name: string]: string } = {
    dashboard: "dashRecurso 3.svg",
    wrench: "mantenimientoRecurso 15.svg",
    inventory: "inventarioRecurso 7.svg",
    calendar: "eventosRecurso 16.svg",
    receipt: "facturacionRecurso 9.svg",
    cash: "cobrarRecurso 10.svg",
    pay: "pagarRecurso 11.svg",
    bank: "bancosRecurso 12.svg",
    book: "ccontabilidadRecurso 14.svg",
    "file-text": "impuestos.svg",
    shield: "autorizador.svg",
  };

  // Devuelve la ruta del SVG nuevo para un icono, o null si no esta migrado.
  iconSrc(name: string): string | null {
    const file = this.menuIconFiles[name];
    return file ? `assets/icons/${file}` : null;
  }

  /** Inyecta navegación, sesión, permisos y el contador compartido del layout. */
  constructor(
    private modalService: NgbModal,
    private router: Router,
    private route: ActivatedRoute,
    public full: FullComponent,
    public requestServ: RequestService,
    public sidebarService: SidebarService,
    private permissionService: PermissionService,
    private aprobacionOrdenCompraServ: AprobacionOrdenCompraWorkspaceService
  ) {}

  @HostListener("window:resize", ["$event"])
  onResize(_: any) {
    this.handleLayout();
  }

  handleLayout() {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth < 992) {
      this.sidebarService.showMinisidebar = true;
    }
  }

  closeSidebarIfMobile() {
    if (this.innerWidth > 768) return;
    this.sidebarService.setMobileOpen(false);
    this.closeFlyout();
  }

  onMenuClick(item: any, ev: MouseEvent, flyout: boolean, level: number) {
    if (!flyout && level === 0 && this.sidebarService.showMinisidebar && this.isTouch && this.hasChildren(item)) {
      ev.preventDefault();
      ev.stopPropagation();
      this.openFlyoutPinned(item, ev);
      return;
    }
    this.toggle(item);
  }

  openFlyoutPinned(item: any, ev: MouseEvent) {
    this.flyoutPinned = true;
    this.openFlyout(item, ev);
  }

  toggleCollapse() {
    this.sidebarService.showMinisidebar = !this.sidebarService.showMinisidebar;
    if (this.sidebarService.showMinisidebar) {
      this.searchTerm = "";
    } else {
      this.closeFlyout();
    }
  }

  /**
   * Hidrata el menú con el tipo vigente de la empresa activa y después aplica
   * los permisos de módulo y usuario antes de habilitar su renderizado; si
   * corresponde inicia el polling de aprobaciones y refleja su total en el ERP.
   */
  async ngOnInit() {
    this.loadingc = true;
    this.handleLayout();

    this.nombre = this.requestServ.master?.usuario?.usuario?.nombres ?? "";
    this.rol = this.requestServ.master?.usuario?.usuario?.tipoUsuario?.nombre
      ?? this.requestServ.master?.usuario?.usuario?.rol
      ?? "Usuario";

    await this.requestServ.refrescarTipoEmpresaActiva();
    this.sections = JSON.parse(JSON.stringify(MENU_SECTIONS));

    // Esperar matriz de permisos authz antes de renderizar: `canShow()` usa
    // PermissionService.can() y si todavia no se cargo, todo el menu queda
    // oculto hasta el primer change detection siguiente.
    try {
      await this.permissionService.loadIfNeeded();
    } catch (_e) {
      /* falla silenciosa: canShow tratara los items con codigoPermiso como ocultos */
    }

    this.applyPermisos();
    this.decorateWithKeysSections();
    this.cargarEmpresas();

    // El badge suma solicitudes de costo de OC y de devoluciones visibles.
    if (
      this.permissionService.can(
        "cuentas_por_pagar.orden_compra.aprobacion.listar"
      ) ||
      this.permissionService.can(
        "cuentas_por_pagar.devolucion_mercaderia.aprobar"
      )
    ) {
      this.aprobacionesContadorSub =
        this.aprobacionOrdenCompraServ.contador$.subscribe((contador) => {
          this.actualizarBadgeAprobaciones(contador.totalAccionable);
        });
      this.aprobacionOrdenCompraServ.iniciarPolling();
    }

    this.expandToActiveRoute();
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.expandToActiveRoute());

    this.loadingc = false;
  }

  /** Libera navegación, badge y polling al destruir el layout autenticado. */
  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.aprobacionesContadorSub?.unsubscribe();
    this.aprobacionOrdenCompraServ.detenerPolling();
  }

  /**
   * Actualiza el badge del enlace de aprobaciones usando el contador SQL; cero
   * se representa sin badge para conservar limpio el árbol de navegación.
   */
  private actualizarBadgeAprobaciones(totalAccionable: number): void {
    for (const section of this.sections) {
      const item = this.buscarPorRuta(
        section.items,
        "/ordenes-compra/aprobaciones"
      );
      if (!item) continue;
      item.badge = Number(totalAccionable) > 0 ? totalAccionable : undefined;
      return;
    }
  }

  /** Busca recursivamente un enlace del menú sin depender de su título visible. */
  private buscarPorRuta(items: RouteInfo[], path: string): RouteInfo | null {
    for (const item of items) {
      if (item.path === path) return item;
      const hijo = item.submenu?.length
        ? this.buscarPorRuta(item.submenu, path)
        : null;
      if (hijo) return hijo;
    }
    return null;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(ev: MouseEvent) {
    if (!this.flyoutRoot || !this.flyoutPinned) return;
    const sidebar = document.querySelector(".left-sidebar");
    const target = ev.target as Node;
    if (sidebar && sidebar.contains(target)) return;
    this.closeFlyout();
  }

  /**
   * Combina los modulos legacy asignados a la empresa con los parametros de
   * la empresa activa que determinan que operaciones aplican a su giro.
   */
  private applyPermisos() {
    const allItems = this.flatTopLevelItems();
    const permisosArr = this.requestServ.master?.permisos ?? [];
    const permisos = new Map<string, boolean>(
      permisosArr.map((p: any) => [p.nombre, !!p.autorizacion])
    );

    this.setAuthByTitle(allItems, "Dashboard", true);

    if (permisos.has("Facturacion")) {
      const fact = this.findByTitle(allItems, "Facturacion");
      if (fact) {
        fact.autorizacion = permisos.get("Facturacion")!;
        fact.title = "Facturación";
        this.facturacion = !!permisos.get("Facturacion");
      }
    }

    if (permisos.has("Cuentas Por Pagar")) {
      this.setAuthByTitle(allItems, "Cuentas por Pagar", permisos.get("Cuentas Por Pagar")!);
    }

    if (permisos.has("Cuentas Por Cobrar")) {
      this.setAuthByTitle(allItems, "Cuentas por Cobrar", permisos.get("Cuentas Por Cobrar")!);
    }

    if (permisos.has("Contabilidad")) {
      const cont = this.findByTitle(allItems, "Contabilidad");
      if (cont) {
        cont.autorizacion = permisos.get("Contabilidad")!;
        (cont as any).xxx = true;
      }
    }

    if (permisos.has("Mantenimiento")) {
      const mant = this.findByTitle(allItems, "Mantenimiento");
      if (mant) {
        mant.autorizacion = permisos.get("Mantenimiento")!;
        const dist = this.findChildByTitle(mant, "Distribucion Propiedades");
        if (dist) (dist as any).isPropiedades = !this.requestServ.empresaUrbanizaciones;
      }
    }

    const invAuth = permisos.get("Inventario y Servicios");
    const servAuth = permisos.get("Servicios");

    const inv = this.findByTitle(allItems, "Inventario/Servicios");
    if (inv) {
      const empresaServicio = !!this.requestServ.empresaServicio;
      const ingresos = this.findChildByTitle(inv, "Ingresos/Egresos");
      const ordenesCompra = this.findChildByTitle(inv, "Órdenes de Compra");
      const autorizacionModulo = empresaServicio
        ? servAuth ?? invAuth
        : invAuth ?? servAuth;

      inv.title = empresaServicio ? "Servicios" : "Inventario/Servicios";
      if (autorizacionModulo != null) {
        inv.autorizacion = !!autorizacionModulo;
      }
      if (ingresos) ingresos.isIventario = empresaServicio;
      if (ordenesCompra) ordenesCompra.isIventario = empresaServicio;
    }

    if (permisos.has("Bancos")) {
      this.setAuthByTitle(allItems, "Bancos", permisos.get("Bancos")!);
    }

    if (permisos.has("Eventos Sociales")) {
      this.setAuthByTitle(allItems, "Eventos Sociales", permisos.get("Eventos Sociales")!);
    }

    if (permisos.has("Reportería")) {
      this.setAuthByTitle(allItems, "Reportes", permisos.get("Reportería")!);
    }

    this.facturacion = this.getModuleAuth("facturacion");
  }

  private flatTopLevelItems(): RouteInfo[] {
    const acc: RouteInfo[] = [];
    for (const s of this.sections) {
      for (const it of s.items) acc.push(it);
    }
    return acc;
  }

  private setAuthByTitle(items: RouteInfo[], title: string, auth: boolean) {
    const it = this.findByTitle(items, title);
    if (it) it.autorizacion = auth;
  }

  private findByTitle(items: RouteInfo[], title: string): RouteInfo | null {
    return items.find((x) => x?.title === title) ?? null;
  }

  private findChildByTitle(parent: RouteInfo, title: string): RouteInfo | null {
    if (!parent?.submenu?.length) return null;
    return parent.submenu.find((x: any) => x?.title === title) ?? null;
  }

  indent(level: number): number {
    return 12 + Math.min(level, 2) * 10;
  }

  private decorateWithKeysSections() {
    for (const s of this.sections) {
      this.decorateWithKeys(s.items, `sect::${s.id}`);
    }
  }

  private decorateWithKeys(items: any[], parentKey: string) {
    items.forEach((it: any, idx: number) => {
      const base = (it.path && String(it.path).trim()) ? String(it.path).trim() : (it.title || "item");
      it.__key = `${parentKey}::${base}::${idx}`;
      if (it.submenu?.length) this.decorateWithKeys(it.submenu, it.__key);
    });
  }

  hasChildren(item: any): boolean {
    return !!item?.submenu?.length;
  }

  toggle(item: any) {
    if (!item?.__key) return;
    if (this.expanded.has(item.__key)) this.expanded.delete(item.__key);
    else this.expanded.add(item.__key);
  }

  isExpanded(item: any): boolean {
    return !!item?.__key && this.expanded.has(item.__key);
  }

  isActive(item: any): boolean {
    if (item?.path) {
      try {
        if (this.router.isActive(item.path, false)) return true;
      } catch {}
    }
    return this.hasChildren(item) ? this.hasActiveDesc(item) : false;
  }

  private hasActiveDesc(item: any): boolean {
    if (!this.hasChildren(item)) return false;
    return item.submenu.some((c: any) => this.isActive(c));
  }

  private expandToActiveRoute() {
    for (const s of this.sections) this.walkAndExpand(s.items);
  }

  private walkAndExpand(items: any[]): boolean {
    let found = false;
    for (const it of items) {
      if (it?.extralink) continue;
      let childFound = false;
      if (this.hasChildren(it)) childFound = this.walkAndExpand(it.submenu);
      const selfActive = it?.path ? this.safeIsActive(it.path) : false;
      if (childFound) this.expanded.add(it.__key);
      if (selfActive || childFound) found = true;
    }
    return found;
  }

  private safeIsActive(path: string): boolean {
    try {
      return this.router.isActive(path, false);
    } catch {
      return false;
    }
  }

  trackByKey = (_: number, item: any) => item.__key;
  trackBySection = (_: number, s: MenuSection) => s.id;

  canShow(item: any): boolean {
    // Flags legacy de items "especiales"
    if (item?.isPropiedades) return false;
    if (item?.isIventario) return false;
    if (item?.isEventos) return !!this.requestServ.empresaEventos;
    if (item?.isFacturacion) {
      return this.facturacion || this.getModuleAuth("facturacion");
    }
    // Legacy: si tiene `autorizacion` booleana explicita, respetar
    if (typeof item?.autorizacion === "boolean" && !item.autorizacion) {
      return false;
    }

    // Authz: chequeo por codigo(s) de permiso (OR si es array)
    if (item?.codigoPermiso) {
      const codigos = Array.isArray(item.codigoPermiso)
        ? item.codigoPermiso
        : [item.codigoPermiso];
      const tieneAlguno = codigos.some((c: string) =>
        this.permissionService.can(c)
      );
      if (!tieneAlguno) return false;
    }

    // Item agrupador: si todos sus hijos quedaron ocultos, ocultar el padre.
    // No aplica si el item tiene path propio (es un link, no solo agrupador).
    if (this.hasChildren(item) && !item?.path) {
      const algunHijoVisible = item.submenu.some((c: any) => this.canShow(c));
      if (!algunHijoVisible) return false;
    }

    return true;
  }

  private norm(s: string): string {
    return (s ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }

  private getModuleAuth(normTitle: string): boolean {
    for (const s of this.sections) {
      const mod = s.items.find((x) => this.norm(x?.title) === normTitle);
      if (mod) return !!mod.autorizacion;
    }
    return false;
  }

  onDisable() {
    this.requestServ.showAlert("No tiene permisos para acceder a este modulo", "error");
  }

  // Search filtering
  matchesSearch(item: RouteInfo): boolean {
    const term = this.norm(this.searchTerm);
    if (!term) return true;
    if (this.norm(item.title).includes(term)) return true;
    if (item.submenu?.length) {
      return item.submenu.some((c) => this.matchesSearch(c));
    }
    return false;
  }

  sectionVisible(section: MenuSection): boolean {
    return section.items.some((it) => this.canShow(it) && this.matchesSearch(it));
  }

  // Initials
  get empresaInitials(): string {
    const emp = this.requestServ.empresa || "";
    const parts = emp.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "?";
  }

  get userInitials(): string {
    const n = (this.nombre || "").trim();
    if (!n) return "?";
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join("");
  }

  // Flyout (mini sidebar)
  openFlyout(item: any, ev: MouseEvent) {
    if (!this.sidebarService.showMinisidebar) return;
    if (!this.hasChildren(item)) { this.closeFlyout(); return; }

    this.cancelFlyoutClose();
    this.flyoutRoot = item;
    this.hoverExpanded.clear();

    const target = ev.currentTarget as HTMLElement;
    const r = target.getBoundingClientRect();

    const sidebarEl = document.querySelector(".left-sidebar") as HTMLElement | null;
    const sb = sidebarEl?.getBoundingClientRect();
    const baseRight = sb?.right ?? r.right;
    this.flyoutLeft = baseRight + 10;
    this.flyoutTop = r.top;

    requestAnimationFrame(() => {
      const fly = document.querySelector(".flyout-overlay") as HTMLElement | null;
      if (!fly) return;
      const h = fly.getBoundingClientRect().height;
      const overflow = (this.flyoutTop + h + 8) - window.innerHeight;
      if (overflow > 0) this.flyoutTop = Math.max(8, this.flyoutTop - overflow);
      if (this.flyoutTop < 8) this.flyoutTop = 8;
    });
  }

  scheduleFlyoutClose() {
    if (this.flyoutPinned) return;
    this.cancelFlyoutClose();
    this.flyoutCloseTimer = setTimeout(() => this.closeFlyout(), 180);
  }

  cancelFlyoutClose() {
    if (this.flyoutCloseTimer) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  closeFlyout() {
    this.cancelFlyoutClose();
    this.flyoutPinned = false;
    this.flyoutRoot = null;
    this.hoverExpanded.clear();
  }

  hoverExpand(item: any) {
    if (!item?.__key || !this.hasChildren(item)) return;
    this.hoverExpanded.add(item.__key);
  }

  hoverCollapse(item: any) {
    if (!item?.__key || !this.hasChildren(item)) return;
    this.hoverExpanded.delete(item.__key);
  }

  isFlyoutOpen(item: any): boolean {
    const hover = !!item?.__key && this.hoverExpanded.has(item.__key);
    return hover || this.isExpanded(item);
  }

  // Empresa selector
  async cargarEmpresas() {
    try {
      const response = await this.requestServ.cargarEmpresas();
      if (response?.[0]) {
        this.empresas = response[1] || [];
      }
    } catch (e) {
      this.empresas = [];
    }
  }

  async cambiarEmpresa(idEmpresa: number) {
    const response = await this.requestServ.cambiarEmpresa(idEmpresa);
    if (response?.[0]) {
      if (this.router.url === "/dashboard") {
        window.location.reload();
      } else {
        this.router.navigateByUrl("/dashboard");
      }
    }
  }

  initials(nombre: string): string {
    if (!nombre) return "?";
    const parts = nombre.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "?";
  }
}
